import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { LifeBuoy, Plus, MessageSquare, Send } from "lucide-react";

const STATUS_COLORS = {
  aberto: "bg-amber-100 text-amber-700",
  respondido: "bg-blue-100 text-blue-700",
  fechado: "bg-green-100 text-green-700",
};

export default function Support() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newOpen, setNewOpen] = useState(false);
  const [form, setForm] = useState({ subject: "", message: "", priority: "media", category: "duvida" });
  const [selected, setSelected] = useState(null);
  const [replyText, setReplyText] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/tickets");
      setTickets(data);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const createTicket = async () => {
    if (!form.subject.trim() || !form.message.trim()) { toast.error("Preenche assunto e mensagem"); return; }
    try {
      await api.post("/tickets", form);
      toast.success("Ticket aberto! A gente responde em breve.");
      setNewOpen(false);
      setForm({ subject: "", message: "", priority: "media", category: "duvida" });
      load();
    } catch (e) { toast.error("Erro"); }
  };

  const openTicket = async (t) => {
    const { data } = await api.get(`/tickets/${t.id}`);
    setSelected(data);
    setReplyText("");
  };

  const addReply = async () => {
    if (!replyText.trim()) return;
    try {
      await api.post(`/tickets/${selected.id}/message`, { message: replyText });
      const { data } = await api.get(`/tickets/${selected.id}`);
      setSelected(data); setReplyText("");
      load();
    } catch (e) { toast.error("Erro"); }
  };

  return (
    <div className="p-5 lg:p-10 max-w-4xl mx-auto" data-testid="support-root">
      <header className="flex justify-between items-start mb-6 gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-[#D4651A] font-bold mb-1">Estamos aqui</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-[#1B3A2F] flex items-center gap-2">
            <LifeBuoy className="w-7 h-7 text-[#D4651A]" /> Suporte
          </h1>
          <p className="text-stone-500 mt-1 text-sm">Abre um chamado, a gente resolve.</p>
        </div>
        <Button data-testid="support-new-btn" onClick={() => setNewOpen(true)} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
          <Plus className="w-4 h-4 mr-1.5" /> Novo chamado
        </Button>
      </header>

      {loading ? (
        <div className="text-stone-500">Carregando…</div>
      ) : tickets.length === 0 ? (
        <Card className="p-10 text-center text-stone-500 rounded-2xl border-dashed border-stone-300">
          <MessageSquare className="w-10 h-10 mx-auto mb-3 text-stone-300" />
          <p>Nenhum chamado ainda.</p>
          <p className="text-xs mt-1">Qualquer coisa que travar, abre aqui que a gente ajuda.</p>
        </Card>
      ) : (
        <div className="space-y-2">
          {tickets.map((t) => (
            <Card
              key={t.id}
              data-testid={`support-ticket-${t.id}`}
              onClick={() => openTicket(t)}
              className="p-4 rounded-xl cursor-pointer hover:shadow-md hover:-translate-y-0.5 transition border-stone-200"
            >
              <div className="flex items-start gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="font-semibold text-sm">{t.subject}</div>
                    <Badge className={STATUS_COLORS[t.status]}>{t.status}</Badge>
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">
                    {t.messages?.length || 0} mensagens · {new Date(t.last_activity_at).toLocaleString("pt-BR")}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* New ticket dialog */}
      <Dialog open={newOpen} onOpenChange={setNewOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Abrir novo chamado</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>Assunto</Label>
              <Input data-testid="support-subject-input" value={form.subject} onChange={(e) => setForm({...form, subject: e.target.value})} placeholder="Ex: Não recebi a mensagem do WhatsApp" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Tipo</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="duvida">Dúvida</SelectItem>
                    <SelectItem value="bug">Problema técnico</SelectItem>
                    <SelectItem value="sugestao">Sugestão</SelectItem>
                    <SelectItem value="cobranca">Cobrança</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Prioridade</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="baixa">Baixa</SelectItem>
                    <SelectItem value="media">Média</SelectItem>
                    <SelectItem value="alta">Alta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Detalhes</Label>
              <Textarea data-testid="support-message-input" rows={5} value={form.message} onChange={(e) => setForm({...form, message: e.target.value})} placeholder="Descreve com calma o que tá acontecendo" className="mt-1" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNewOpen(false)}>Cancelar</Button>
            <Button data-testid="support-submit-btn" onClick={createTicket} className="bg-[#1B3A2F] hover:bg-[#244A3C]">Abrir chamado</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle>{selected.subject}</DialogTitle>
                <div className="flex gap-2 mt-2">
                  <Badge className={STATUS_COLORS[selected.status]}>{selected.status}</Badge>
                  <Badge variant="outline">prio: {selected.priority}</Badge>
                </div>
              </DialogHeader>
              <div className="space-y-3 my-4">
                {selected.messages?.map((m) => (
                  <div key={m.id} className={`p-3 rounded-lg text-sm ${m.from === "user" ? "bg-[#D9FDD3]" : "bg-stone-100 border-l-4 border-[#D4651A]"}`}>
                    <div className="text-[10px] text-stone-500 uppercase tracking-wider font-semibold mb-1">
                      {m.from === "user" ? "Você" : `Suporte${m.admin_name ? ` (${m.admin_name})` : ''}`} · {new Date(m.created_at).toLocaleString("pt-BR")}
                    </div>
                    <div className="whitespace-pre-wrap text-stone-700">{m.message}</div>
                  </div>
                ))}
              </div>
              {selected.status !== "fechado" && (
                <div className="space-y-2 border-t border-stone-100 pt-4">
                  <Textarea
                    data-testid="support-reply-input"
                    rows={3}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Adicionar mensagem..."
                  />
                </div>
              )}
              <DialogFooter>
                <Button variant="outline" onClick={() => setSelected(null)}>Fechar</Button>
                {selected.status !== "fechado" && (
                  <Button data-testid="support-add-msg-btn" onClick={addReply} disabled={!replyText.trim()} className="bg-[#1B3A2F] hover:bg-[#244A3C]">
                    <Send className="w-3.5 h-3.5 mr-1.5" /> Enviar
                  </Button>
                )}
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
